import type { ErrorComponentProps } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_E164 } from '@/lib/constants';

export function RouterErrorBoundary({ reset, error }: ErrorComponentProps) {
  const handleGoHome = () => {
    // Use window.location for reliable navigation in error scenarios
    window.location.href = '/0';
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-12">
      <div className="border-error/20 rounded-lg border bg-rose-100 p-8 text-center">
        <h1 className="text-error mb-4 text-2xl font-semibold sm:text-3xl">Something went wrong</h1>
        <p className="text-charcoal-900 mb-6 text-lg">
          We're sorry, but something unexpected happened.
        </p>
        <p className="text-charcoal-700 mb-8 text-base">
          You can try again or start over. If this keeps happening, please call us at{' '}
          <a href={`tel:${SUPPORT_PHONE_E164}`} className="font-semibold underline">
            {SUPPORT_PHONE_DISPLAY}
          </a>
          .
        </p>
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
          <Button onClick={reset} variant="default" size="lg">
            Try Again
          </Button>
          <Button onClick={handleGoHome} variant="outline" size="lg">
            Start Over
          </Button>
        </div>

        {import.meta.env.MODE === 'development' && error && (
          <details className="mt-8 text-left">
            <summary className="text-error cursor-pointer text-sm font-medium">
              Technical Details (Development Only)
            </summary>
            <pre className="text-charcoal-700 mt-2 overflow-auto rounded bg-rose-100 p-4 text-xs">
              {error.stack ? error.stack : error.toString()}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
